import { Apis, Labels } from '../../app.constants';
import SweetAlert from '../../../components/Shared/Notification';
import APIService from '../../APIService';
import { buildReportScopeQuery, normalizeReportScope } from './reportScope';

const ReportActions = () => {
  const getReportTemplates = async (scopeOrLeadFormId, publishedOnly) => {
    try {
      const scope = normalizeReportScope(scopeOrLeadFormId);
      const query = buildReportScopeQuery(scope, {
        published_only: scope.published_only ?? publishedOnly,
      });
      const res = await APIService.get(`${Apis.getReportTemplate}?${query}`);
      return res;
    } catch (error) {
      return error;
    }
  };

  const getHtmlPreview = async (leadFormId, payload) => {
    try {
      const res = await APIService.post(`${Apis.htmlPreview}${leadFormId}/`, payload);
      return res;
    } catch (error) {
      SweetAlert('Failed to fetch html preview', 'error');
      return error;
    }
  };

  const downloadReport = async (templateId, payload, fileName = 'report.xlsx') => {
    try {
      return await APIService.downloadFilePOST(
        `${Apis.htmlPreview}${templateId}/`,
        payload,
        fileName
      );
    } catch (error) {
      SweetAlert('Failed to download report', 'error');
      return false;
    }
  };

  const deleteReport = async (templateId) => {
    try {
      const res = await APIService.remove(`${Apis.deleteReport}${templateId}`);
      return res;
    } catch (error) {
      return error;
    }
  };

  const getReportTemplateDetails = async (templateId) => {
    if (!templateId) {
      return null;
    }
    try {
      const res = await APIService.get(`${Apis.getTemplate}${templateId}`);
      return res;
    } catch (error) {
      return error;
    }
  };

  const updateReportTemplate = async (templateId, payload) => {
    if (!templateId) return { status: 400, data: { message: 'Template ID required' } };
    try {
      const res = await APIService.put(`${Apis.updateReport}${templateId}`, payload);
      return res;
    } catch (error) {
      SweetAlert('Failed to update report template', 'error');
      return error?.response ?? { status: 500, data: { message: 'Network/Server error' } };
    }
  };

  const generateReportTemplates = async (payload) => {
    try {
      const res = await APIService.post(`${Apis.generateReport}report-templates/`, payload);
      return res;
    } catch (error) {
      return error;
    }
  };

  const generateQuickReport = async (payload) => {
    try {
      const res = await APIService.post(`${Apis.generateReport}reports/quick`, payload);
      return res;
    } catch (error) {
      return error;
    }
  };

  const getSchemaHelper = async (payload) => {
    try {
      const res = await APIService.post(`${Apis.generateReport}schema-helper`, payload);
      return res;
    } catch (error) {
      SweetAlert('Failed to load schema helper', 'error');
      return error;
    }
  };

  const getReportAnalysis = async (scopeOrLeadFormId, options = {}) => {
    try {
      const query = buildReportScopeQuery(scopeOrLeadFormId, {
        limit: 20,
        ...options,
      });
      const res = await APIService.get(`${Apis.generateReport}reports/generated?${query}`);
      return res;
    } catch (error) {
      SweetAlert('Failed to fetch report analysis', 'error');
      return error;
    }
  };

  const getScheduleOptions = async () => {
    try {
      const res = await APIService.get(Apis.scheduleOptions);
      return res;
    } catch (error) {
      SweetAlert('Failed to load schedule options', 'error');
      return error;
    }
  };

  const createTemplateTrigger = async (templateId, payload) => {
    if (!templateId) return { status: 400, data: { message: 'Template ID required' } };
    try {
      const url = `${Apis.templateTriggers}${templateId}/triggers/`;
      const res = await APIService.post(url, payload);
      return res;
    } catch (error) {
      SweetAlert('Failed to create trigger', 'error');
      return error?.response ?? { status: 500, data: { message: 'Network/Server error' } };
    }
  };

  const getTemplateTriggers = async (templateId) => {
    if (!templateId) return { status: 400, data: { message: 'Template ID required' } };
    try {
      const url = `${Apis.templateTriggers}${templateId}/triggers/`;
      const res = await APIService.get(url);
      return res;
    } catch (error) {
      SweetAlert('Failed to fetch triggers', 'error');
      return error?.response ?? { status: 500, data: { message: 'Network/Server error' } };
    }
  };

  const updateTemplateTrigger = async (templateId, triggerId, payload) => {
    if (!templateId || !triggerId) {
      return { status: 400, data: { message: 'Template ID and Trigger ID required' } };
    }
    try {
      const url = `${Apis.templateTriggers}${templateId}/triggers/${triggerId}/`;
      const res = await APIService.put(url, payload);
      return res;
    } catch (error) {
      SweetAlert('Failed to update trigger', 'error');
      return error?.response ?? { status: 500, data: { message: 'Network/Server error' } };
    }
  };

  return {
    getReportTemplates,
    getHtmlPreview,
    downloadReport,
    deleteReport,
    getReportTemplateDetails,
    updateReportTemplate,
    generateReportTemplates,
    generateQuickReport,
    getSchemaHelper,
    getReportAnalysis,
    getScheduleOptions,
    createTemplateTrigger,
    getTemplateTriggers,
    updateTemplateTrigger,
  };
};
export { ReportActions };
